import { Injector, Pipe, PipeTransform, inject } from '@angular/core'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { DynamicElementSpecification } from '../../models/dynamic-element/dynamic-element-specification'
import { DynamicElements } from '../../services/dynamic-elements/dynamic-elements'

/**
 * A pipe that will convert a provided {@link DynamicElementSpecification} into the appropriate
 * matching HTML content.
 *
 * @public
 *
 * @example
 * Example Script
 * ```
 * let dynamicElement: DynamicElement = {
 *   id: '12345',
 *   type: 'Div',
 *   classes: 'd-block',
 *   data: { foo: 'bar' },
 *   children: [
 *     {
 *       id: '12346',
 *       type: 'Paragraph',
 *       classes: '',
 *       data: { text: 'hello' },
 *       children: []
 *     }
 *   ]
 * }
 * ```
 *
 * Example Template
 * ```
 * <div [innerHTML]="dynamicElement | gpeiDynamicElementToHtml"></div>
 * ```
 */
@Pipe({
  name: 'gpeiDynamicElementToHtml',
  standalone: true
})
export class DynamicElementToHtmlPipe implements PipeTransform {

  private readonly _injector = inject(Injector)
  private readonly _dynamicElements = inject(DynamicElements)
  private readonly _domSanitizer = inject(DomSanitizer)

  /**
   * Convert the provided value into a safe HTML value.
   *
   * @param value The target DynamicElement or collection of DynamicElement that will be created
   *              and turned into HTML
   *
   * @returns     The HTML content of the DynamicElement or collection of DynamicElement
   */
  transform(value: DynamicElementSpecification | DynamicElementSpecification[]): SafeHtml {
    let pendingValue = value instanceof Array ?
      value :
      [ value ]

    let htmlString = pendingValue
      .map(it => this._dynamicElements.build(this._injector, it))
      .filter(it => it != null)
      .map(it => it.outerHTML)
      .join('\n')

    return this._domSanitizer.bypassSecurityTrustHtml(htmlString) // NOSONAR This is a risk that those using the pipe should be aware of because of it's nature
  }

}
